import { FC, useState, useEffect } from "react";
import "./ChatMessage.scss";
import AgentStreamingMessage from "../AgentStreamingMessage/AgentStreamingMessage";
import MarkdownMessage from "../../MarkdownMessage/MarkdownMessage";
import HtmlMessage from "../../HtmlMessage/HtmlMessage";
import {
  ChatMessageArticleAssociationModel,
  ChatMessageModel,
} from "../../../models/Chat/ChatMessageModel";
import ChatMessageVote from "../ChatMessageVote/ChatMessageVote";
import ChatQuiz from "../ChatQuiz/ChatQuiz";
import moment from "moment";
import { BvIcon } from "bevi-icon";
import api from "../../../services/Api";
import useIsMobileApp from "../../../hooks/useIsMobileApp";

interface ChatMessageProps {
  key?: number;
  message: ChatMessageModel;
  isUser?: boolean;
  articles?: ChatMessageArticleAssociationModel[];
  isLast?: boolean;
  /** Resumo das respostas do formulário (substitui o quiz quando já respondido). */
  quizAnswers?: string;
  /** Esconde o cabeçalho de categoria (resposta após o formulário = resposta única). */
  hideTitle?: boolean;
}

const hasHtmlTable = (content: string) => content.includes("<table");

// Remove o cabeçalho de categoria ("[SIAPE]\n" ou "<h3>SIAPE</h3>") do início do
// conteúdo — usado para a resposta após o formulário parecer uma resposta única.
const stripCategoryHeader = (text?: string): string => {
  if (!text) return text ?? "";
  return text
    .replace(/^\s*<h3>[\s\S]*?<\/h3>\s*\n?/, "")
    .replace(/^\s*\[[^\]\n]+\]\s*\n?/, "");
};

const getFileIcon = (fileName: string): string => {
  const ext = fileName.split(".").pop()?.toLowerCase() || "";

  const iconMap: Record<string, string> = {
    pdf: "pdf",
    doc: "description",
    docx: "description",
    xls: "table_chart",
    xlsx: "table_chart",
    ppt: "presentation",
    pptx: "presentation",
    txt: "description",
    csv: "table_chart",
    jpg: "image",
    jpeg: "image",
    png: "image",
    gif: "image",
    webp: "image",
    zip: "folder_zip",
    rar: "folder_zip",
  };

  return iconMap[ext] || "attachFile";
};

const AuthenticatedImage: FC<{ src: string; alt: string; style?: React.CSSProperties }> = ({ src, alt, style }) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);

  useEffect(() => {
    const loadImage = async () => {
      try {
        if (src.startsWith("blob:")) {
          setImageSrc(src);
          return;
        }

        const fullUrl = src.startsWith("http") ? src : `${process.env.REACT_APP_BEVIAJUDA_API?.replace(/\/$/, "")}${src}`;
        const response = await api.get(fullUrl, { responseType: "blob" });
        const blobUrl = URL.createObjectURL(response.data);
        setImageSrc(blobUrl);
      } catch (error) {
        console.error("Erro ao carregar imagem:", error);
      }
    };

    loadImage();
  }, [src]);

  if (!imageSrc) return null;
  return <img src={imageSrc} alt={alt} style={style} />;
};

const handleDownloadFile = async (fileUrl: string, fileName: string) => {
  try {
    const response = await fetch(fileUrl);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Erro ao baixar arquivo:", error);
  }
};

const ChatMessage: FC<ChatMessageProps> = ({
  key,
  message,
  isUser,
  articles,
  isLast,
  quizAnswers,
  hideTitle,
}) => {
  // No mobile os artigos da resposta aparecem, mas não abrem.
  const isChatOnly = useIsMobileApp();
  const displayContent = hideTitle
    ? stripCategoryHeader(message.content ?? "")
    : (message.content ?? "");
  const displayFull = hideTitle
    ? stripCategoryHeader(message.fullContent ?? "")
    : (message.fullContent ?? "");

  return (
    <div
      key={message.id ?? key}
      className="ChatMessage chat-message"
      data-testid="ChatMessage"
      style={isUser ? { display: 'flex', flexDirection: 'column' } : {}}
    >
      {message?.files && message.files.length > 0 && isUser && (
        <div className="chat-message__files mb-2" style={{ marginLeft: 'auto' }}>
          <div className="chat-message__files-list">
            {message.files.map((file, key2) => {
              const isImage = file.is_image !== undefined ? file.is_image : file.filename?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

              return (
                <div key={`file-${file.id ?? key2}`}>
                  {isImage ? (
                    <div
                      onClick={() => handleDownloadFile(file.url, file.filename)}
                      style={{ cursor: 'pointer', display: 'inline-block' }}
                      title={`Clique para baixar ${file.filename}`}
                    >
                      <AuthenticatedImage
                        src={file.url || ''}
                        alt={file.filename}
                        style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '8px', marginBottom: '12px', objectFit: 'contain' }}
                      />
                    </div>
                  ) : (
                    <button
                      className="chat-message__file-item"
                      onClick={() => handleDownloadFile(file.url, file.filename)}
                      title={`Baixar ${file.filename}`}
                    >
                      <BvIcon name={getFileIcon(file.filename)} size={2} />
                      <span className="chat-message__file-name">{file.filename}</span>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div
        className={`chat-message ${isUser ? "user-message" : "agent-message"}`}
      >
        {(message?.content || message?.fullContent) && (
          <div className="message-content my-3">
            {message?.isStreaming &&
              message?.content === "" &&
              message?.fullContent ? (
              <AgentStreamingMessage
                fullText={displayFull}
                key={message.id ?? key}
                createdAt={message.created_at ?? ""}
              />
            ) : (
              <>
                {hasHtmlTable(displayContent) ? (
                  <HtmlMessage content={displayContent} />
                ) : (
                  <MarkdownMessage
                    content={displayContent}
                    createdAt={message.created_at ?? ""}
                  />
                )}

                {!isUser && message?.quiz && (
                  <ChatQuiz
                    quiz={message.quiz}
                    active={!!isLast && !quizAnswers}
                    answersSummary={quizAnswers}
                  />
                )}

                {!isUser &&
                  !!articles &&
                  articles.length === 0 &&
                  message.created_at &&
                  message?.isStreaming && (
                    <p className="text-xs text-muted mt--14">
                      {moment(message.created_at).format("DD/MM/YYYY, HH:mm")}
                    </p>
                  )}

                {!isUser && !!articles && articles.length > 0 && (
                  <div>
                    <p className="text-information mt-3 mb-1">
                      {isChatOnly ? "Artigos utilizados na resposta:" : "Clique e leia mais sobre os artigos utilizados na resposta:"}
                    </p>

                    <div className="article-carousel mb-2">
                      {articles.map((item: any, i: number) =>
                        isChatOnly ? (
                          <div
                            key={item?.id ?? i}
                            className="article-card text-decoration-none text-dark"
                          >
                            <h6 className="m-0">
                              {item?.title ?? item?.article?.title}
                            </h6>
                          </div>
                        ) : (
                          <a
                            key={item?.id ?? i}
                            href={`/articles/view/${item && item?.article?.id
                              ? item?.article?.id
                              : item?.id
                              }`}
                            className="article-card text-decoration-none text-dark"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <h6 className="m-0">
                              {item?.title ?? item?.article?.title}
                            </h6>
                          </a>
                        )
                      )}
                    </div>

                    {message.created_at && (
                      <p className="text-xs text-muted">
                        {moment(message.created_at).format("DD/MM/YYYY, HH:mm")}
                      </p>
                    )}
                  </div>
                )}

                {message?.files && message.files.length > 0 && !isUser && (
                  <div className="chat-message__files mt-3">
                    <p className="chat-message__files-label">Anexos:</p>
                    <div className="chat-message__files-list">
                      {message.files.map((file, idx) => (
                        <button
                          key={file?.id ?? idx}
                          className="chat-message__file-item"
                          onClick={() => handleDownloadFile(file.url, file.filename)}
                          title={`Baixar ${file.filename}`}
                        >
                          <BvIcon name={getFileIcon(file.filename)} size={2} />
                          <span className="chat-message__file-name">{file.filename}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {!isUser && message?.id && !message?.quiz && (
                  <>
                    <hr className="separator-actions" />
                    <ChatMessageVote
                      messageId={message.id}
                      vote={message?.vote}
                      voted={message?.voted}
                    />
                  </>
                )}
              </>
            )}
          </div>
        )}

        {isUser && message.created_at && (
          <p className="text-xs text-muted pe-3">
            {moment(message.created_at).format("DD/MM/YYYY, HH:mm")}
          </p>
        )}
      </div>
    </div>
  );
};

export default ChatMessage;
